// How an offering's sessions are booked (setting D): when needed, in fixed
// weekly slots held with one coach, or the member's choice. Shared by the
// service (pack / training plan) editor and a plan's included bookables.
export interface SchedulingValue {
  scheduling: 'on_demand' | 'fixed_recurring' | 'member_choice';
  slotsPerWeek: string;
  slotDurationMin: string;
}

export const schedulingDefaults = (r?: { scheduling?: string; slotsPerWeek?: number | null; slotDurationMin?: number | null } | null): SchedulingValue => ({
  scheduling: (r?.scheduling as SchedulingValue['scheduling']) ?? 'on_demand',
  slotsPerWeek: r?.slotsPerWeek ? String(r.slotsPerWeek) : '',
  slotDurationMin: r?.slotDurationMin ? String(r.slotDurationMin) : '',
});

/** The body fields the API takes (routes/services.ts, member-plans class-access). */
export const schedulingBody = (v: SchedulingValue) => ({
  scheduling: v.scheduling,
  slotsPerWeek: v.scheduling === 'on_demand' || !v.slotsPerWeek ? null : Number(v.slotsPerWeek),
  slotDurationMin: v.scheduling === 'on_demand' || !v.slotDurationMin ? null : Number(v.slotDurationMin),
});

export default function SchedulingFields({ value, onChange, trainerMode }: { value: SchedulingValue; onChange: (v: SchedulingValue) => void; trainerMode: string }) {
  const needsCoach = trainerMode === 'any';
  const set = (patch: Partial<SchedulingValue>) => onChange({ ...value, ...patch });
  return (
    <>
      <div className="col-span-2">
        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">How sessions are booked</label>
        <select value={needsCoach ? 'on_demand' : value.scheduling} disabled={needsCoach} onChange={e => set({ scheduling: e.target.value as SchedulingValue['scheduling'] })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white disabled:opacity-50">
          <option value="on_demand">When needed — booked from the Book tab</option>
          <option value="fixed_recurring">Fixed weekly slots — chosen at purchase, held every week</option>
          <option value="member_choice">Member’s choice — fixed slots or book as they go</option>
        </select>
        <p className="text-[11px] text-gray-400 mt-1">
          {needsCoach
            ? 'Fixed weekly slots are held with one coach — set “Who delivers it” to member picks or gym assigns first.'
            : value.scheduling === 'on_demand'
              ? 'Members book each session themselves.'
              : 'At checkout the member sees only times the coach is free every week of the plan. Pause, leave and coach changes act on the slot.'}
        </p>
      </div>
      {!needsCoach && value.scheduling !== 'on_demand' && (
        <>
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Weekly slots</label>
            <input type="number" min={1} max={7} value={value.slotsPerWeek} onChange={e => set({ slotsPerWeek: e.target.value })} placeholder="Up to the weekly allowance" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white disabled:opacity-50" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Slot length (min)</label>
            <input type="number" min={15} max={480} step={5} value={value.slotDurationMin} onChange={e => set({ slotDurationMin: e.target.value })} placeholder="The bookable’s length" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white disabled:opacity-50" />
          </div>
        </>
      )}
    </>
  );
}
